import { hasExplicitLogout } from "../utils/authStorage"
import axios from "axios"

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8005/api",
  timeout: 300000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true
})


// Retry configuration
const MAX_RETRIES = 3
const RETRY_DELAY = 1000 // ms
const RETRY_BACKOFF_MULTIPLIER = 2
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504]

/**
 * Calculate exponential backoff delay
 * @param {number} retryCount - Current retry attempt number
 * @returns {number} Delay in milliseconds
 */
const getRetryDelay = (retryCount) => {
  return RETRY_DELAY * Math.pow(RETRY_BACKOFF_MULTIPLIER, retryCount - 1)
}

/**
 * Check if request should be retried
 * @param {*} error - Axios error object
 * @returns {boolean} Whether request should be retried
 */
const shouldRetry = (error) => {
  if (!error.response) {
    // Network errors should be retried
    return true
  }
  // Retry on specific status codes
  return RETRYABLE_STATUS_CODES.includes(error.response.status)
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (hasExplicitLogout()) return Promise.reject(new axios.CanceledError("You signed out. Sign in again."))
    // Initialize retry count
    config.retryCount = config.retryCount || 0
    
    const token = localStorage.getItem("token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    // console.error("[v0] Request configuration error:", error)
    return Promise.reject(error)
  },
)

// Response interceptor for error handling and retry logic
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === "development") {
      // console.log("[v0] API Response:", response.config.method.toUpperCase(), response.config.url, response.status)
    }
    return response
  },
  async (error) => {
    const config = error.config
    if (hasExplicitLogout()) return Promise.reject(error)

    // Handle authentication errors
    if (error.response?.status === 401) {
      // Phase 5 — a 401 that surfaces while the device is offline (or the
      // request never really reached the server) doesn't mean the session
      // is actually invalid, just that we can't verify it right now. Don't
      // wipe the token/redirect in that case — the caller's own
      // offline-queue handling will decide what to do, and the session
      // will get re-verified the next time the app is back online.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return Promise.reject(error)
      }

      // Try one silent token refresh before giving up on the session — the
      // access token may have just expired mid-audit while the refresh
      // cookie is still valid. Only retry the original request once.
      if (config && !config.__isRetryAfterRefresh && !config.url?.includes("/auth/refresh-token")) {
        try {
          await api.post("/auth/refresh-token", {})
          config.__isRetryAfterRefresh = true
          return api(config)
        } catch (refreshError) {
          // Refresh failed too — fall through to the logout below.
        }
      }

      window.dispatchEvent(new Event("auth:unauthorized"))
      return Promise.reject(error)
    }

    // Handle forbidden errors
    if (error.response?.status === 403) {
      // console.warn("[v0] Access forbidden")
      return Promise.reject(error)
    }

    // Implement retry logic
    // if (config && config.retryCount < MAX_RETRIES && shouldRetry(error)) {
    //   config.retryCount++
    //   const delay = getRetryDelay(config.retryCount)
      
    //   // console.warn(`[v0] Retrying request (attempt ${config.retryCount}/${MAX_RETRIES}) after ${delay}ms:`, config.url)
      
    //   // Wait before retrying
    //   await new Promise(resolve => setTimeout(resolve, delay))
      
    //   // Retry the request
    //   return api(config)
    // }

    // Log final error
    // console.error("[v0] API Error:", {
    //   status: error.response?.status,
    //   url: error.config?.url,
    //   method: error.config?.method,
    //   message: error.response?.data?.message || error.message,
    //   errors: error.response?.data?.errors,
    // })

    return Promise.reject(error)
  },
)

export default api
